import React, { useRef, useState } from 'react';

import { Box, ButtonBase, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import clsx from 'clsx';

import { CompanyRoleDTO } from '@vyce/core/src/api/types';

import { getRoleTier, useRoleChipStyles } from '../utils/roleChip';
import { getRoleDescription } from '../utils/roleDescriptions';

const NO_ACCESS_LABEL = 'No access';

interface Props {
  /** module_system_name — picks the plain-English description shown under each role. */
  moduleSystemName?: string;
  value: CompanyRoleDTO | null;
  unresolvedRoleName?: string;
  options: CompanyRoleDTO[];
  disabled?: boolean;
  onChange: (role: CompanyRoleDTO | null) => void;
}

export const RoleChipDropdown: React.FC<Props> = ({
  moduleSystemName,
  value,
  unresolvedRoleName,
  options,
  disabled,
  onChange,
}) => {
  const chipClasses = useRoleChipStyles();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const label = unresolvedRoleName || value?.role_name || NO_ACCESS_LABEL;
  const tier = unresolvedRoleName ? 'basic' : getRoleTier(value?.role_name || null);

  const handleSelect = (role: CompanyRoleDTO | null) => {
    setOpen(false);
    onChange(role);
  };

  const chip = (
    <ButtonBase
      ref={anchorRef}
      disabled={disabled}
      onClick={() => setOpen(true)}
      className={clsx(chipClasses.chip, chipClasses[tier])}>
      {label}
      {!disabled && (
        <Box display="inline-flex" marginLeft={0.5}>
          <FiChevronDown size={13} />
        </Box>
      )}
    </ButtonBase>
  );

  return (
    <>
      {unresolvedRoleName ? (
        <Tooltip title={`Role “${unresolvedRoleName}” isn't available for this company anymore`}>
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem selected={!value && !unresolvedRoleName} onClick={() => handleSelect(null)}>
          <Box flex={1} marginRight={2}>
            {NO_ACCESS_LABEL}
          </Box>
          {!value && !unresolvedRoleName && <FiCheck size={14} />}
        </MenuItem>
        {options.map(role => {
          const description = getRoleDescription(moduleSystemName, role);
          return (
            <MenuItem
              key={role.role_id}
              selected={role.role_id === value?.role_id}
              onClick={() => handleSelect(role)}>
              <Box flex={1} marginRight={2}>
                {role.role_name}
                {!!description && (
                  <Box
                    fontSize={11.5}
                    lineHeight={1.45}
                    color="text.secondary"
                    whiteSpace="normal"
                    maxWidth={280}>
                    {description}
                  </Box>
                )}
              </Box>
              {role.role_id === value?.role_id && <FiCheck size={14} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
